import ArrowLeft from '@ixigo/icons/ArrowLeft';
import ChevronLeft from '@ixigo/icons/ChevronLeft';
import LocationOn from '@ixigo/icons/LocationOn';
import NearMe from '@ixigo/icons/NearMe';
import Swap from '@ixigo/icons/Swap';
import ButtonBase from '@ixigo/iui/ButtonBase';
import IconButton from '@ixigo/iui/IconButton';
import Input, { type InputProps } from '@ixigo/iui/Input';
import { ListItemText } from '@ixigo/iui/ListItemText';
import Typography from '@ixigo/iui/Typography';
import { useEventCallback } from '@ixigo/iui/utils';
import {
	ResponsiveModal,
	type ResponsiveModalProps,
} from '@repo/shared/components';
import { Loader } from '@repo/shared/icons';
import cn from '@repo/shared/utils/cn';
import {
	Fragment,
	useDeferredValue,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { getUserLocation, isCoords } from '../location-services';
import { MapLocationPicker, type MapLocationPickerProps } from '../map';
import { TRIP_TYPE, TRIP_TYPE_CONFIG, type TripType } from '../trip-type';
import type { Coords, MapLocation } from '../types';
import { getLocations, getPlaceDetails } from './api';
import { Combobox } from './Combobox';
import type { LocationSuggestion } from './types';
import { type UseComboboxParams, useCombobox } from './useCombobox';
import { groupItemsBySection } from './utils';

interface LocationPickerProps {
	onClose: () => void;
	activeField?: 'pickup' | 'drop';
	defaultValue?: { pickup?: LocationSuggestion; drop?: LocationSuggestion };
	placeholders?: { pickup?: string; drop?: string };
	/**
	 * Explicit trip type — decides which field is DB-airport sourced.
	 * Toggling the in-sheet swap button swaps field values.
	 */
	tripType?: TripType;
	onComplete: (value: {
		pickup?: LocationSuggestion;
		drop?: LocationSuggestion;
		tripType: TripType;
	}) => void;
	mapApiKey?: MapLocationPickerProps['apiKey'];
}

export interface LocationPickerSheetProps extends LocationPickerProps {
	open: boolean;
	TransitionProps?: ResponsiveModalProps['TransitionProps'];
}

export function LocationPickerSheet({
	open,
	onClose,
	TransitionProps,
	...props
}: LocationPickerSheetProps) {
	useEffect(() => {
		// Request location permission early (cached, with fallback)
		getUserLocation().catch(() => {});
	}, []);

	return (
		<ResponsiveModal
			open={open}
			onClose={onClose}
			fullScreen
			transitionDirection="right"
			TransitionProps={TransitionProps}
		>
			<LocationPicker {...props} onClose={onClose} />
		</ResponsiveModal>
	);
}

// minQueryLength 0 so an empty input still hits the API — the backend returns
// recent suggestions / nearby airports for an empty query.
const loadOptions = {
	debounceMs: 300,
	minQueryLength: 0,
} satisfies UseComboboxParams<unknown>['loadOptions'];

function getPreloadKeys({
	pickup,
	drop,
}: {
	pickup?: LocationSuggestion;
	drop?: LocationSuggestion;
}): Array<string | undefined> {
	return [pickup, drop].map((location) =>
		isCoords(location) ? `${location.lat},${location.lng}` : location?.placeId,
	);
}

export function LocationPicker(props: LocationPickerProps) {
	const {
		onClose,
		defaultValue,
		onComplete,
		activeField = 'pickup',
		placeholders = TRIP_TYPE_CONFIG[
			props.tripType ?? TRIP_TYPE.AIRPORT_DROP_OFF
		].placeholders,
		mapApiKey,
	} = props;
	const [tripType, setTripType] = useState<TripType>(
		props.tripType ?? TRIP_TYPE.AIRPORT_DROP_OFF,
	);
	// Selected values are controlled here so a trip-type toggle can swap
	// them in place — no remount needed to reflect the new values
	const [values, setValues] = useState<{
		pickup?: LocationSuggestion;
		drop?: LocationSuggestion;
	}>({ pickup: defaultValue?.pickup, drop: defaultValue?.drop });
	const [active, setActive] = useState<'pickup' | 'drop'>(activeField);
	const [showMap, setShowMap] = useState(false);

	const { airportField } = TRIP_TYPE_CONFIG[tripType];

	// Loaders below are wrapped in useEventCallback (its ref updates via a plain
	// useEffect, after preload effects fire). When tripType changes, useAsyncList's
	// preload effect can re-run BEFORE the loader's ref catches up — the call
	// would send the old tripType to the BE. Mirror tripType in a layout-effect
	// ref so the loaders always read the current value.
	const tripTypeRef = useRef(tripType);
	useLayoutEffect(() => {
		tripTypeRef.current = tripType;
	}, [tripType]);

	// Always called with the opposite trip type (see toggleTripType), so there's
	// no same-value case to guard. Cross-cutting rule: toggling swaps From ↔ To
	// values, whether fields are filled or empty.
	const handleTripTypeChange = (next: TripType) => {
		setTripType(next);
		setValues((current) => ({ pickup: current.drop, drop: current.pickup }));
		setActive(TRIP_TYPE_CONFIG[next].airportField);
	};

	const toggleTripType = () =>
		handleTripTypeChange(
			tripType === TRIP_TYPE.AIRPORT_DROP_OFF
				? TRIP_TYPE.AIRPORT_PICK_UP
				: TRIP_TYPE.AIRPORT_DROP_OFF,
		);

	// Cache of resolved coords (placeId → coords); survives toggles since the
	// component is no longer remounted
	const latestSelectionCoords = useRef<
		Partial<
			Record<
				'pickup' | 'drop',
				Promise<(LocationSuggestion & Coords) | undefined>
			>
		>
	>({});

	// Each field's suggestions are biased by the OTHER field's coords, so its
	// preload key is the other field's value; changing it refetches suggestions.
	const preloadKeys = getPreloadKeys(values);
	// tripType also decides each field's role (airport vs location), so it must
	// be part of the preload key — otherwise toggling trip type with empty
	// fields leaves the key unchanged and the stale (old-role) list persists.
	const preloadKey = (otherFieldKey?: string) => `${tripType}:${otherFieldKey}`;

	// Resolve coords from placeId
	const resolveCoords = useEventCallback(
		async (type: 'pickup' | 'drop', newLocation?: LocationSuggestion) => {
			const location =
				newLocation ??
				(latestSelectionCoords.current[type] ? undefined : values[type]);
			if (location && isMapLocation(location)) {
				latestSelectionCoords.current[type] = isCoords(location)
					? Promise.resolve(location)
					: getPlaceDetails(location.placeId).catch(() => undefined);
			}
			return latestSelectionCoords.current[type];
		},
	);

	const handleSelect = useEventCallback(
		(field: 'pickup' | 'drop', value?: LocationSuggestion) => {
			if (!value) return;
			const newValues = { ...values, [field]: value };
			setValues(newValues);
			const other = field === 'pickup' ? 'drop' : 'pickup';
			if (!newValues[other]) {
				resolveCoords(field, value);
				setActive(other);
				fieldConfig[other].inputRef.current?.focus();
				return;
			}
			onClose();
			onComplete({ ...newValues, tripType });
		},
	);

	// Stable load functions - no dependencies on coords

	// Loaders return items + section meta (categories, zero-state displayMessage).
	// They read tripType from the layout-effect ref above so a query that fires
	// in the same commit as a tripType toggle still sees the new value.
	const loadPickup = useEventCallback(
		async (query: string, opts: { signal: AbortSignal }) => {
			const dropOffCoords = await resolveCoords('drop');
			const { items, categories, displayMessage } = await getLocations(query, {
				...opts,
				tripType: tripTypeRef.current,
				inputFieldType: 'PICKUP',
				dropOffCoords,
			});
			return { items, meta: { categories, displayMessage } };
		},
	);
	const loadDrop = useEventCallback(
		async (query: string, opts: { signal: AbortSignal }) => {
			const pickupCoords = await resolveCoords('pickup');
			const { items, categories, displayMessage } = await getLocations(query, {
				...opts,
				tripType: tripTypeRef.current,
				inputFieldType: 'DROP',
				pickupCoords,
			});
			return { items, meta: { categories, displayMessage } };
		},
	);

	// Known limitation: iui's `useControlledState` (inside `useCombobox`) locks
	// controlled-vs-uncontrolled mode at first render based on whether `value`
	// is `!== undefined`. We open the picker with both fields empty
	// (`values.pickup`/`drop` = undefined) so the hook locks as uncontrolled and
	// later prop updates from `setValues` (e.g. on a trip-type swap) are ignored —
	// the input keeps showing the previously-selected value visually. The picker's
	// own `values` state is still correct (and `onComplete` fires from there) so
	// nothing wrong gets submitted; the bug is a stale display that recovers as
	// soon as the user touches the input. Repro: open picker → fill ONE field →
	// tap in-sheet swap. Not fixing today; a sentinel value would resolve it but
	// adds surface for "did we forget to convert sentinel → undefined" defects.
	const pickup = useCombobox({
		load: loadPickup,
		itemToString: (item) => item.mainText,
		value: values.pickup,
		onChange: ({ value }) => handleSelect('pickup', value),
		loadOptions: { ...loadOptions, preload: preloadKey(preloadKeys[1]) },
		// Nearby airports surface via preload (keyed on the other field's coords),
		// so we don't clear-on-focus — that would route the same data through the
		// query channel and mask the refreshed preload after the other field
		// changes.
		clearOnFocus: false,
	});

	const drop = useCombobox({
		load: loadDrop,
		itemToString: (item) => item.mainText,
		value: values.drop,
		onChange: ({ value }) => handleSelect('drop', value),
		loadOptions: { ...loadOptions, preload: preloadKey(preloadKeys[0]) },
		clearOnFocus: false,
	});

	// create a record of active field and its combobox
	const fieldConfig = {
		pickup: pickup,
		drop: drop,
	};

	function getInputProps(field: 'pickup' | 'drop'): InputProps {
		const props = fieldConfig[field].getInputProps({
			onFocus: () => setActive(field),
		});
		return {
			...props,
			endAdornment: fieldConfig[field].loading ? (
				<Loader className="text-selection icon-md" />
			) : (
				<Combobox.ClearButton {...fieldConfig[field].getClearProps()} />
			),
			autoFocus: field === active,
			variant: 'outlined',
			placeholder: placeholders?.[field],
			className: 'col-start-2 min-w-0',
			classes: {
				outline: cn('rounded-l-full rounded-r-full border-secondary'),
				// Active field's placeholder is the lighter disabled grey (iui default);
				// the non-active field shows a darker secondary placeholder (per design)
				input: field === active ? undefined : 'placeholder:text-secondary',
			},
		};
	}

	const {
		items,
		meta,
		getListProps,
		getItemProps,
		loading,
		query = '',
	} = fieldConfig[active];

	const isAirportActive = active === airportField;
	const displayMessage = meta?.displayMessage;
	const sections = groupItemsBySection(items, meta?.categories ?? []);
	const AirportIcon = TRIP_TYPE_CONFIG[tripType].flightIcon;
	const showZeroState = useDeferredValue(
		!loading && items.length === 0 && (!!displayMessage || query.length >= 1),
	);

	return (
		<div className="flex h-full flex-col overflow-hidden">
			{/* Inputs section */}
			<div className="relative py-5 px-20 shrink-0 items-start gap-x-1 gap-y-2.5 grid grid-rows-2 grid-cols-[max-content_minmax(0,1fr)] sticky top-0">
				<IconButton
					className="self-center row-start-1 col-start-1"
					edge="start"
					onClick={onClose}
				>
					<ChevronLeft />
				</IconButton>
				<Input {...getInputProps('pickup')} />
				<Input {...getInputProps('drop')} />
				<ButtonBase
					onClick={toggleTripType}
					style={{ width: 32, height: 32, right: 52 }}
					className="absolute z-10 top-1/2 -translate-y-1/2 rounded-full bg-primary border border-secondary flex items-center justify-center"
					aria-label="Swap pickup and destination"
				>
					<Swap
						style={{ width: 26, height: 26 }}
						className="-rotate-90 text-disabled"
					/>
				</ButtonBase>
			</div>

			<div className="flex-1 overflow-y-auto">
				{/* Use current location row — map free-pick is not allowed for the airport field */}
				{mapApiKey && !isAirportActive && (
					<Combobox.Item onClick={() => setShowMap(true)}>
						<Combobox.Icon>
							<LocationOn />
						</Combobox.Icon>
						<ListItemText primary="Set location on map" />
						<NearMe />
					</Combobox.Item>
				)}

				<div {...getListProps()} style={{ paddingTop: 16, paddingBottom: 16 }}>
					{showZeroState && (
						<Typography
							variant="body-sm"
							className="block px-20 py-10 text-secondary text-center"
						>
							{displayMessage ?? 'No results'}
						</Typography>
					)}
					{sections.map((section) => (
						<div key={section.key}>
							{/* Backend-driven section heading */}
							{section.title ? (
								<div className="pl-20 py-5px">
									<Typography variant="h6" style={{ letterSpacing: '-0.18px' }}>
										{section.title}
									</Typography>
								</div>
							) : null}
							{section.items.map((item, index) => {
								const { mainText, secondaryText, type } = item;
								return (
									<Fragment key={`${mainText}-${index}`}>
										<Combobox.Item {...getItemProps({ item })}>
											<Combobox.Icon>
												{type === 'airport' ? <AirportIcon /> : <LocationOn />}
											</Combobox.Icon>
											<ListItemText
												primary={mainText}
												secondary={
													secondaryText ? (
														<Typography
															variant="body-xs"
															className="text-secondary"
														>
															{secondaryText}
														</Typography>
													) : undefined
												}
											/>
										</Combobox.Item>
										{/* Inset divider between rows; hidden after the last via last:hidden */}
										<div className="border-t border-tertiary mx-20 last:hidden" />
									</Fragment>
								);
							})}
						</div>
					))}
				</div>
			</div>
			<ResponsiveModal
				open={showMap}
				onClose={() => setShowMap(false)}
				fullScreen
				transitionDirection="right"
			>
				<MapLocationPicker
					onConfirm={(location) => {
						fieldConfig[active].selectValue({ item: location });
						setShowMap(false);
					}}
					apiKey={mapApiKey}
					defaultValue={
						isMapLocation(fieldConfig[active].value)
							? fieldConfig[active].value
							: undefined
					}
					title={
						active === 'pickup'
							? 'Set your pick-up location'
							: 'Set your destination'
					}
				/>
				<IconButton
					className="bg-primary absolute left-20 top-3 shadow-100 rounded-full"
					onClick={() => setShowMap(false)}
				>
					<ArrowLeft />
				</IconButton>
			</ResponsiveModal>
		</div>
	);
}

function isMapLocation(location?: LocationSuggestion): location is MapLocation {
	return !!(location && (isCoords(location) || location.placeId));
}

export default LocationPickerSheet;
